import React from 'react';

/**
 * HandoffBadge — small contextual pill on a visit row.
 *
 * Renders nothing when the visit has no active handler. Otherwise:
 *   • If viewer IS the active handler → amber "Handling" pill (they
 *     should expect to see the visit in their queue because they're
 *     doing a delegated procedure on it).
 *   • If viewer is the visit's primary doctor and the handler is
 *     someone else → slate "Handed off" pill (muted, informational).
 *   • Otherwise (admin / receptionist / bystander) → slate "Handoff"
 *     pill so they know someone else is actively working.
 */
export default function HandoffBadge({ visit, viewerMemberId, isDark }) {
  const handler = visit?.active_handler_member_id;
  if (!handler) return null;

  const isHandlerMe = viewerMemberId && handler === viewerMemberId;
  const isDoctorMe = viewerMemberId && visit?.doctor_member_id === viewerMemberId;

  let label;
  let colorBg;
  let colorFg;
  let colorBorder;
  if (isHandlerMe) {
    label = 'Handling';
    colorBg = isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)';
    colorFg = isDark ? '#fbbf24' : '#d97706';
    colorBorder = isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.25)';
  } else if (isDoctorMe) {
    label = 'Handed off';
    colorBg = isDark ? 'rgba(148,163,184,0.12)' : 'rgba(100,116,139,0.08)';
    colorFg = isDark ? '#94a3b8' : '#64748b';
    colorBorder = isDark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.2)';
  } else {
    label = 'Handoff';
    colorBg = isDark ? 'rgba(148,163,184,0.12)' : 'rgba(100,116,139,0.08)';
    colorFg = isDark ? '#94a3b8' : '#64748b';
    colorBorder = isDark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.2)';
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.5625rem',
        fontWeight: 600,
        padding: '0.0625rem 0.375rem',
        borderRadius: '0.375rem',
        background: colorBg,
        color: colorFg,
        border: `1px solid ${colorBorder}`,
        whiteSpace: 'nowrap',
      }}
      title={(() => {
        // Phase 1c — enrich tooltip with the handler's name/role when we
        // have it on the visit row.
        const handlerName = visit?.active_handler_name || '';
        const handlerRole = visit?.active_handler_role || '';
        const who = [handlerRole, handlerName].filter(Boolean).join(' ') || null;
        if (isHandlerMe) return 'You are the active handler on this visit';
        if (isDoctorMe) {
          return who
            ? `Handed off to ${who}`
            : 'Another clinician is handling a procedure on this visit';
        }
        return who
          ? `Currently with ${who}`
          : 'A delegated procedure is in progress';
      })()}
    >
      🤝 {label}
    </span>
  );
}
